import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { format, addWeeks, startOfWeek, isEqual, isAfter } from 'date-fns';
import { getTeamWeeklySessions } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import EventBlock from "../../components/EventBlock";

function WeeklyView({ weekNum, selectedMembers, today, onWeekClick, isAdminView }) {
    const [weeklySessions, setWeeklySessions] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const { groupInfo } = useAuth();
    const [maxHeight, setMaxHeight] = useState(722);
    const blockRefs = useRef([]);

    useEffect(() => {
        async function fetchSessions() {
            if (!groupInfo) return;

            setIsLoading(true);
            try {
                const sessionsPromises = Array.from({ length: 5 }, async (_, index) => {
                    const weekStart = addWeeks(startOfWeek(groupInfo.startDate, { weekStartsOn: 1 }), weekNum + index);
                    
                    // Skip weeks that are beyond the course end date
                    if (groupInfo.endDate && isAfter(weekStart, groupInfo.endDate)) {
                        return { weekStart: format(weekStart, 'yyyy-MM-dd'), sessions: [] };
                    }
                    
                    const sessions = await getTeamWeeklySessions(weekStart, groupInfo.groupId);
                    return { weekStart: format(weekStart, 'yyyy-MM-dd'), sessions };
                });

                const results = await Promise.all(sessionsPromises);
                const sessionsMap = results.reduce((acc, { weekStart, sessions }) => {
                    acc[weekStart] = sessions;
                    return acc;
                }, {});

                setWeeklySessions(sessionsMap);
            } catch (error) {
                console.error("Error fetching sessions:", error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchSessions();
    }, [groupInfo, weekNum]);

    useEffect(() => {
        const heights = blockRefs.current.map(ref => ref?.offsetHeight || 0);
        const maxBlockHeight = Math.max(...heights);
        setMaxHeight(maxBlockHeight);
    }, [weeklySessions]);

    const filterSessionsByContributor = useCallback((sessions) => {
        if (selectedMembers.length === 0) return sessions;
        return sessions.filter(session =>
                session.contributors && session.contributors.some(contributor =>
                    selectedMembers.some(member => member.email === contributor.email)
                )
        );
    }, [selectedMembers]);

    return (
        <BlockWrapper>
            {Array.from({ length: 5 }, (_, index) => {
                const weekStart = addWeeks(
                    startOfWeek(groupInfo?.startDate || new Date(), { weekStartsOn: 1 }),
                    weekNum + index
                );
                
                // Check if week is outside course range
                const isOutsideRange = groupInfo?.endDate && isAfter(weekStart, groupInfo.endDate);
                
                const fullDate = format(weekStart, 'yyyy-MM-dd');
                const filteredSessions = filterSessionsByContributor(weeklySessions[fullDate] || []);
                const isToday = isEqual(
                    startOfWeek(weekStart, { weekStartsOn: 1 }),
                    startOfWeek(today, { weekStartsOn: 1 })
                );

                return (
                    <EventBlock
                        key={fullDate}
                        ref={el => blockRefs.current[index] = el}
                        isToday={isToday}
                        date={weekStart}
                        sessions={filteredSessions}
                        maxHeight={maxHeight}
                        viewType="week"
                        weekNum={weekNum + index + 1}
                        onWeekClick={onWeekClick}
                        isOutsideRange={isOutsideRange}
                        isAdminView={isAdminView}
                    />
                );
            })}
        </BlockWrapper>
    );
}

const BlockWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    overflow-x: auto;
    gap: 20px;
`;

export default WeeklyView;